/**
 * WeekOverrideModal — per-week capacity override (holidays, short weeks).
 *
 * Adjust:
 *   - Working days for THIS week (default from settings)
 *   - Workers off (holiday, sick leave) — subtracted from base capacity
 *   - Entire teams off — click to toggle
 *   - Note (e.g. "Elvis + Bogdan on holiday")
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, X, CalendarOff, Trash2 } from "lucide-react";
import { API, authHeader, inputCls, Field, ModalShell, TEAM_COLOR } from "./shared";

export default function WeekOverrideModal({ week, teams, defaultWorkingDays, onClose, onSaved }) {
  const [form, setForm] = useState({
    working_days: defaultWorkingDays,
    workers_off: 0,
    teams_off: [],
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/week-overrides?weeks=${encodeURIComponent(week)}`, authHeader());
        const found = (data.overrides || [])[0];
        if (found) {
          setForm({
            working_days: found.working_days ?? defaultWorkingDays,
            workers_off: found.workers_off || 0,
            teams_off: found.teams_off || [],
            note: found.note || "",
          });
          setHasExisting(true);
        }
      } catch { /* no override yet */ }
    })();
  }, [week, defaultWorkingDays]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/week-overrides`, {
        week,
        working_days: form.working_days === "" ? null : parseInt(form.working_days),
        workers_off: parseInt(form.workers_off) || 0,
        teams_off: form.teams_off,
        note: form.note,
      }, authHeader());
      toast.success(`${week} capacity updated`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Remove capacity override for ${week}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/week-overrides/${week}`, authHeader());
      toast.success(`${week} back to default capacity`);
      onSaved();
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  const toggleTeam = (id) => setForm((f) => ({
    ...f,
    teams_off: f.teams_off.includes(id)
      ? f.teams_off.filter((t) => t !== id)
      : [...f.teams_off, id],
  }));

  const totalWorkersOff = parseInt(form.workers_off || 0)
    + teams.filter((t) => form.teams_off.includes(t.id)).reduce((s, t) => s + (parseInt(t.standard_size) || 0), 0);

  return (
    <ModalShell onClose={onClose} maxWidth="max-w-lg" testid="week-override-modal">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-black flex items-center gap-1">
            <CalendarOff className="w-3 h-3"/> Adjust capacity
          </div>
          <h2 className="text-lg font-black text-slate-100">{week}</h2>
          <div className="text-[11px] text-slate-400 mt-1">
            Reduce this week&apos;s capacity when workers or teams are away.
          </div>
        </div>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-100"><X className="w-5 h-5"/></button>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Working days (this week only)" hint={`Default: ${defaultWorkingDays}`}>
            <input type="number" min="0" max="7" value={form.working_days}
                   data-testid="ov-working-days"
                   onChange={(e) => setForm({ ...form, working_days: e.target.value })}
                   className={inputCls}/>
          </Field>
          <Field label="Workers off (holiday)" hint="Subtracted from base worker count">
            <input type="number" min="0" value={form.workers_off}
                   data-testid="ov-workers-off"
                   onChange={(e) => setForm({ ...form, workers_off: e.target.value })}
                   className={inputCls}/>
          </Field>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">Teams off this week</div>
          <div className="flex flex-wrap gap-1.5">
            {teams.map((t) => {
              const off = form.teams_off.includes(t.id);
              return (
                <button key={t.id} type="button" onClick={() => toggleTeam(t.id)}
                        data-testid={`ov-team-${t.id}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition
                          ${off
                            ? "border-red-400/60 bg-red-500/20 text-red-100 line-through"
                            : "border-white/10 bg-white/5 text-slate-300"}`}>
                  <span className={`w-2 h-2 rounded-full ${TEAM_COLOR[t.color] || "bg-slate-400"}`}/>
                  {t.name}
                </button>
              );
            })}
            {teams.length === 0 && <div className="text-xs text-slate-500 italic">No active teams</div>}
          </div>
        </div>

        <Field label="Reason / note">
          <textarea rows={2} value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Elvis + Bogdan on holiday..."
                    className={inputCls} data-testid="ov-note"/>
        </Field>

        <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Effective reduction</div>
          <div className="text-2xl font-black text-slate-100 mt-1">
            −{totalWorkersOff} workers
          </div>
          <div className="text-[10px] text-slate-400">
            × {form.working_days || 0} days = <strong className="text-amber-200">−{(totalWorkersOff * (parseInt(form.working_days) || 0)).toFixed(0)} worker-days</strong>
          </div>
        </div>
      </div>

      <div className="px-6 py-3 border-t border-white/10 flex justify-between gap-2">
        <div>
          {hasExisting && (
            <button onClick={remove} disabled={busy} data-testid="ov-remove"
                    className="px-4 py-2 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 text-xs font-black uppercase flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5"/> Remove
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs font-black uppercase">
            Cancel
          </button>
          <button onClick={submit} disabled={busy} data-testid="ov-save"
                  className="px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-black uppercase flex items-center gap-1">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Save className="w-3.5 h-3.5"/> Save</>}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
